import { z } from "zod"

/**
 * Рекламная система CPA/CPM (этап 8, пакет П1).
 *
 * CPM — оплата за 1000 показов по ставке продавца; CPA — процент с
 * подтверждённой продажи по пакету («Базовый», «Расширенный», «Максимальный
 * охват»). Кампания проходит модерацию площадки, затем товары кампании
 * подмешиваются в выдачу витрины с меткой «Реклама». Заказ засчитывается
 * рекламе по последнему клику в окне атрибуции; CPA-комиссия удерживается из
 * начислений продавцу после доставки и холда.
 */

export const AdModelSchema = z.enum(["cpa", "cpm"])
export type AdModel = z.infer<typeof AdModelSchema>

export const AdPackageSchema = z.enum(["basic", "extended", "maximum"])
export type AdPackage = z.infer<typeof AdPackageSchema>

export const AdPlacementSchema = z.enum(["catalog", "search", "recommendations", "home", "email"])
export type AdPlacement = z.infer<typeof AdPlacementSchema>

export const AdCampaignStatusSchema = z.enum([
  "draft",
  "pending_moderation",
  "approved",
  "rejected",
  "active",
  "paused",
  "completed",
])
export type AdCampaignStatus = z.infer<typeof AdCampaignStatusSchema>

export const AdAttributionStateSchema = z.enum(["pending", "held", "charged", "reversed"])
export type AdAttributionState = z.infer<typeof AdAttributionStateSchema>

export const AdTargetingSchema = z.object({
  /** Категории, в выдаче которых показывается кампания; пусто — везде. */
  categoryIds: z.array(z.string().min(1).max(64)).max(50).default([]),
  /** Города покупателя (справочник «География»); пусто — все регионы. */
  regionIds: z.array(z.string().min(1).max(120)).max(100).default([]),
})
export type AdTargeting = z.infer<typeof AdTargetingSchema>

// ── Настройки площадки ─────────────────────────────────────────────────────

export const AdPackageSettingsSchema = z.object({
  /** Доля: 0.05 = 5 %. */
  rate: z.number().min(0).max(1),
  minBudget: z.number().min(0),
  placements: z.array(AdPlacementSchema).min(1),
})
export type AdPackageSettings = z.infer<typeof AdPackageSettingsSchema>

export const AdSettingsSchema = z.object({
  cpaPackages: z.object({
    basic: AdPackageSettingsSchema,
    extended: AdPackageSettingsSchema,
    maximum: AdPackageSettingsSchema,
  }),
  /** Минимальная ставка CPM, руб. за 1000 показов. */
  cpmMinBid: z.number().min(0),
  /** Минимальный бюджет CPM-кампании, руб. */
  cpmMinBudget: z.number().min(0),
  /** Окно атрибуции: сколько дней после клика заказ засчитывается рекламе. */
  attributionWindowDays: z.number().int().min(1).max(90),
  /** Холд после доставки до списания CPA-комиссии, дней. */
  holdDays: z.number().int().min(0).max(60),
  /** Срок модерации кампании, часов. */
  moderationSlaHours: z.number().int().min(1).max(720),
})
export type AdSettings = z.infer<typeof AdSettingsSchema>

export const AdSettingsPatchSchema = z
  .object({
    cpaPackages: z
      .object({
        basic: AdPackageSettingsSchema.partial(),
        extended: AdPackageSettingsSchema.partial(),
        maximum: AdPackageSettingsSchema.partial(),
      })
      .partial(),
    cpmMinBid: z.number().min(0),
    cpmMinBudget: z.number().min(0),
    attributionWindowDays: z.number().int().min(1).max(90),
    holdDays: z.number().int().min(0).max(60),
    moderationSlaHours: z.number().int().min(1).max(720),
  })
  .partial()
export type AdSettingsPatch = z.infer<typeof AdSettingsPatchSchema>

// ── Кампания ───────────────────────────────────────────────────────────────

const moment = z.string().min(1).max(40)

export const AdCampaignInputSchema = z.object({
  title: z.string().trim().min(1).max(120),
  description: z.string().max(1000).optional(),
  bannerUrl: z.string().max(1000).nullable().optional(),
  model: AdModelSchema,
  /** Обязателен для CPA, у CPM не передаётся. */
  package: AdPackageSchema.optional(),
  /** Ставка CPM, руб. за 1000 показов. У CPA ставку задаёт пакет. */
  bid: z.number().min(0).optional(),
  budgetTotal: z.number().min(0),
  budgetDaily: z.number().min(0).nullable().optional(),
  startsAt: moment,
  endsAt: moment,
  targeting: AdTargetingSchema.optional(),
  productIds: z.array(z.string().min(1)).min(1).max(200),
})
export type AdCampaignInput = z.infer<typeof AdCampaignInputSchema>

export const AdCampaignPatchSchema = AdCampaignInputSchema.omit({ model: true })
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: "Нечего менять" })
export type AdCampaignPatch = z.infer<typeof AdCampaignPatchSchema>

export const AdRejectInputSchema = z.object({
  reason: z.string().trim().min(3, "Укажите причину отказа").max(1000),
})
export type AdRejectInput = z.infer<typeof AdRejectInputSchema>

export type AdCampaignProduct = {
  id: string
  name: string
  photo: string | null
  price: number
  status: string
  categoryId: string | null
}

export type AdCampaign = {
  id: string
  shopId: string
  shopName?: string
  title: string
  description: string
  bannerUrl: string | null
  model: AdModel
  package: AdPackage | null
  /** CPA — доля, CPM — руб. за 1000 показов. */
  rate: number
  budgetTotal: number
  budgetDaily: number | null
  spent: number
  startsAt: string
  endsAt: string
  targeting: AdTargeting
  status: AdCampaignStatus
  completedReason: "budget" | "ends" | "manual" | null
  rejectReason: string | null
  moderatedBy: string | null
  moderatedAt: string | null
  submittedAt: string | null
  slaDueAt: string | null
  productIds: string[]
  products?: AdCampaignProduct[]
  createdAt: string
  updatedAt: string
}

// ── Показы и клики (витрина) ──────────────────────────────────────────────

export const AdEventInputSchema = z.object({
  campaignId: z.string().min(1).max(64),
  productId: z.string().min(1).max(64),
  kind: z.enum(["impression", "click"]),
  placement: AdPlacementSchema,
})
export type AdEventInput = z.infer<typeof AdEventInputSchema>

export const AdEventsBatchSchema = z.object({
  /** Анонимный идентификатор устройства — тот же, что у событий витрины. */
  visitor: z.string().trim().min(3).max(128),
  events: z.array(AdEventInputSchema).max(100),
})
export type AdEventsBatch = z.infer<typeof AdEventsBatchSchema>

export type AdEventsResult = {
  accepted: number
  ignored: number
  /** CPM-кампании, исчерпавшие бюджет этой пачкой показов. */
  completed?: string[]
}

/** Рекламная позиция под место показа: товар + кампания. */
export type AdPlacementItem = {
  campaignId: string
  productId: string
  model: AdModel
  package: AdPackage | null
}

// ── Отчётность ─────────────────────────────────────────────────────────────

export type AdStatsTotals = {
  impressions: number
  clicks: number
  /** Клики / показы; null без показов. */
  ctr: number | null
  conversions: number
  /** Выручка засчитанных рекламе позиций (без отменённых и возвращённых). */
  revenue: number
  /** Списано за рекламу за период. */
  spend: number
  /** (выручка − расход) / расход; null без расхода. */
  roi: number | null
}

export type AdStatsDay = AdStatsTotals & { date: string }

export type AdCampaignStats = {
  campaignId: string
  from: string
  to: string
  totals: AdStatsTotals
  byDay: AdStatsDay[]
}

export type AdSummary = {
  from: string
  to: string
  totals: AdStatsTotals
  campaigns: { total: number; active: number; pendingModeration: number }
  byCampaign: (AdStatsTotals & {
    campaignId: string
    title: string
    status: AdCampaignStatus
    model: AdModel
    shopId: string
    /** Только в отчёте консоли. */
    shopName?: string
  })[]
}

export type AdAttribution = {
  orderId: string
  orderNumber: number | null
  orderLineId: string
  productId: string
  productName: string
  shopId: string
  shopName?: string
  campaignId: string
  campaignTitle: string
  model: AdModel
  clickEventId: string | null
  clickedAt: string
  orderCreatedAt: string
  amountBase: number
  rate: number
  charge: number
  state: AdAttributionState
  heldAt: string | null
  chargedAt: string | null
  reversedAt: string | null
  reverseReason: string | null
}

// ── Рекламные баллы (PDF заказчика п.4, 27.10.2026) ─────────────────────────
//
// 1 балл = 1 руб. Начисляются приветственным бонусом (новому магазину по
// продавцовому коду и рефереру) и корректировкой консоли, живут 30 дней с
// начисления, тратятся только на рекламу — раньше удержания из выплат.

export const AdPointsEntryKindSchema = z.enum(["grant", "spend", "refund", "expire", "adjust"])
export type AdPointsEntryKind = z.infer<typeof AdPointsEntryKindSchema>

export const AdPointsLotSourceSchema = z.enum(["welcome", "referral", "adjust"])
export type AdPointsLotSource = z.infer<typeof AdPointsLotSourceSchema>

export type AdPointsLot = {
  id: string
  source: AdPointsLotSource
  amount: number
  remaining: number
  grantedAt: string
  expiresAt: string
  comment: string
}

export type AdPointsBalance = {
  shopId: string
  /** Доступно сейчас, баллов (= руб.). */
  balance: number
  /** Ближайшее сгорание: сколько и когда. */
  nextExpiry: { amount: number; at: string } | null
  /** Магазину начислен приветственный бонус — для тултипа «Вам начислено…». */
  welcome: { amount: number; grantedAt: string; expiresAt: string } | null
  /** Живые партии (остаток > 0), раньше сгорающие — первыми. */
  lots: AdPointsLot[]
}

export type AdPointsEntry = {
  id: string
  kind: AdPointsEntryKind
  delta: number
  balanceAfter: number
  createdAt: string
  comment: string
  campaignId: string | null
  campaignTitle: string | null
  actorName: string | null
  /** Начисление: источник и срок партии. */
  source: AdPointsLotSource | null
  expiresAt: string | null
}

/**
 * Чем магазин может оплатить запуск кампании: баллы плюс прогнозируемые
 * рублёвые начисления (ещё не выведенный остаток к выплате).
 */
export type AdFunds = {
  points: number
  projected: number
  total: number
}

export const AdPointsAdjustInputSchema = z.object({
  /** Плюс — начислить (новая партия на 30 дней), минус — списать по FIFO. */
  delta: z.number().finite().refine((v) => v !== 0, "Укажите сумму корректировки"),
  reason: z.string().trim().min(3, "Укажите причину корректировки").max(500),
})
export type AdPointsAdjustInput = z.infer<typeof AdPointsAdjustInputSchema>
